
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, RealtimePayload } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface TransactionsDetailProps {
  period: "month" | "quarter" | "year";
  startDate: string;
  endDate: string;
}

export function TransactionsDetail({ period, startDate, endDate }: TransactionsDetailProps) {
  const [activeTab, setActiveTab] = useState<"income" | "expenses">("income");
  const queryClient = useQueryClient();
  const [channelName] = useState(`detail-updates-${Date.now()}`);
  
  useEffect(() => {
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transactions' },
        (payload: RealtimePayload) => {
          console.log('TransactionsDetail detected transaction change:', payload.eventType);
          queryClient.invalidateQueries({ 
            queryKey: ["transactions-detail", activeTab, period, startDate, endDate] 
          });
        }
      )
      .subscribe((status) => {
        console.log('TransactionsDetail real-time subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeTab, period, startDate, endDate, queryClient, channelName]);

  const { data: transactions, isLoading } = useQuery({
    queryKey: ["transactions-detail", activeTab, period, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from("transactions")
        .select(`
          *,
          customers (
            name
          )
        `)
        .eq("type", activeTab === "income" ? "entrada" : "saida")
        .order("created_at", { ascending: false });

      if (startDate) {
        query = query.gte("created_at", startDate);
      }
      if (endDate) {
        query = query.lte("created_at", endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
    staleTime: 10000,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <Tabs value={activeTab} onValueChange={(value: "income" | "expenses") => setActiveTab(value)}>
      <TabsList className="w-full justify-start bg-[#1A1E2C] border-b border-[#404040] p-0 h-12">
        <TabsTrigger
          value="income"
          className="data-[state=active]:bg-[#0073B7] data-[state=active]:text-white px-8 h-12 rounded-none"
        >
          Entradas
        </TabsTrigger>
        <TabsTrigger
          value="expenses"
          className="data-[state=active]:bg-[#0073B7] data-[state=active]:text-white px-8 h-12 rounded-none"
        >
          Saídas
        </TabsTrigger>
      </TabsList>

      <TabsContent value="income" className="mt-4">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#404040] hover:bg-[#1A1E2C]">
              <TableHead className="text-[#8A9099] font-semibold">Data</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Cliente</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Descrição</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Valor</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions?.map((transaction) => (
              <TableRow
                key={transaction.id}
                className="border-b border-[#404040] hover:bg-[#1A1E2C]"
              >
                <TableCell className="font-medium text-white">
                  {format(new Date(transaction.created_at), "dd/MM/yyyy")}
                </TableCell>
                <TableCell className="text-white">
                  {transaction.customers?.name}
                </TableCell>
                <TableCell className="text-white">
                  {transaction.description}
                </TableCell>
                <TableCell className="text-white font-medium">
                  {formatCurrency(transaction.amount)}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      transaction.payment_status === "pago"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}
                  >
                    {transaction.payment_status}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabsContent>

      <TabsContent value="expenses" className="mt-4">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#404040] hover:bg-[#1A1E2C]">
              <TableHead className="text-[#8A9099] font-semibold">Data</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Categoria</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Descrição</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Valor</TableHead>
              <TableHead className="text-[#8A9099] font-semibold">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions?.map((transaction) => (
              <TableRow
                key={transaction.id}
                className="border-b border-[#404040] hover:bg-[#1A1E2C]"
              >
                <TableCell className="font-medium text-white">
                  {format(new Date(transaction.created_at), "dd/MM/yyyy")}
                </TableCell>
                <TableCell className="text-white">
                  {transaction.category}
                </TableCell>
                <TableCell className="text-white">
                  {transaction.description}
                </TableCell>
                <TableCell className="text-white font-medium">
                  {formatCurrency(transaction.amount)}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      transaction.payment_status === "pago"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}
                  >
                    {transaction.payment_status}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TabsContent>
    </Tabs>
  );
}
